import { useEffect, useState } from "react";
import { BOOK_GOODS_MAX, storeLinks, type GoodsProduct } from "@greg-chick/shared";
import { absArtUrl, loadGoodsDoc, loadManifest } from "../lib/contentApi";

/**
 * 📖→🛍️ **다 읽은 뒤 보여줄 굿즈 고르기** — 전자책 편집 화면 안.
 *
 * 이 서비스는 책에서 굿즈로 잇는 게 목적인데 둘이 서로를 몰랐다. 어느 책에 무엇을 붙일지는
 * **이야기를 아는 사람**이 정해야 한다 — 코드가 정할 일이 아니다.
 *
 * ## 화면에서 지키는 것
 * - **최대 3개.** 마지막 쪽이 카탈로그가 되면 안 된다(판정은 shared `BOOK_GOODS_MAX`)
 * - **살 수 있는 곳이 없는 굿즈는 그렇다고 적는다.** 붙여도 아이 화면엔 안 나온다 —
 *   "붙였는데 왜 안 보이지"를 미리 막는다
 * - 굿즈가 하나도 없으면 칸 자체를 안 그린다(할 수 있는 게 없는 칸을 보여주지 않는다)
 */
export function BookGoodsPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [products, setProducts] = useState<GoodsProduct[] | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    void Promise.all([loadGoodsDoc(), loadManifest()]).then(([doc, m]) => {
      if (!alive) return;
      setProducts(doc.products);
      const map: Record<string, string> = {};
      for (const p of doc.products) {
        const key = p.images[0];
        const url = key ? absArtUrl(m.assets[key]?.url) : undefined;
        if (url) map[p.id] = url;
      }
      setThumbs(map);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!products || products.length === 0) return null;

  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else if (selected.length < BOOK_GOODS_MAX) onChange([...selected, id]);
  };

  return (
    <section className="bk-sec bkgoods">
      <h3 className="bk-sec__h">
        다 읽은 뒤 보여줄 굿즈 <span className="bkgoods__count">{selected.length}/{BOOK_GOODS_MAX}</span>
      </h3>
      <p className="hint">
        마지막 쪽에서 아이에게 보여줘요. 안 고르면 아무것도 안 나와요 — 그것도 괜찮아요.
      </p>
      <ul className="bkgoods__list">
        {products.map((p) => {
          const on = selected.includes(p.id);
          const buyable = storeLinks(p).length > 0;
          const full = !on && selected.length >= BOOK_GOODS_MAX;
          return (
            <li key={p.id}>
              <button
                type="button"
                className={`bkgoods__item${on ? " is-on" : ""}`}
                onClick={() => toggle(p.id)}
                disabled={full}
                title={full ? `${BOOK_GOODS_MAX}개까지 고를 수 있어요` : undefined}
              >
                {thumbs[p.id] ? (
                  <img src={thumbs[p.id]} alt="" loading="lazy" />
                ) : (
                  <span className="bkgoods__noimg" aria-hidden="true">
                    🎁
                  </span>
                )}
                <span className="bkgoods__name">{p.name_ko}</span>
                {on ? <span className="bkgoods__check" aria-hidden="true">✓</span> : null}
              </button>
              {on && !buyable ? (
                <p className="bkgoods__warn">살 수 있는 곳이 없어서 아이 화면엔 안 나와요.</p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
